import React, {
  useContext,
  useEffect,
  useState,
  FunctionComponent,
} from 'react'
import accounting from 'accounting'
import { useSelector } from 'react-redux'
import { get } from 'lodash'
import { DateTime } from 'luxon'
import { DateTimeDisplay, PageHeading } from '../../../components'
import Table from '../../../components/Table/next'
import ReportActions from '../../../components/ReportActions'
import { TransactionsTableContext } from './index'
import { TransactionDetail } from '../../../typings/detailReport'
import { State } from '../../../reducers'
import { UserStore } from '../../../typings/stores/UserStore'
import { prepareDatesForApi } from '../../../utils/dates'
import { TransactionDetailsReportVariables } from './index'
import TableFilters from '../Filters/TableFilters'
import { dumpData } from '../../../actions/reports'
import { decode } from 'he'

interface TransactionsTableProps {
  data: any
  loading: boolean
  error: boolean
  getData: ({ variables }) => void
  exportData: any
  exportLoading: boolean
  exportError: boolean
  getExportData: ({ variables }) => void
}

const TransactionsTable: FunctionComponent<TransactionsTableProps> = ({
  getData,
  data,
  loading,
  error,
  getExportData,
  exportData,
  exportLoading,
  exportError,
}) => {
  const user = useSelector<State, UserStore>(state => state.user)
  const {
    filters,
    pagination: { size, page },
    sort,
    changePageSize,
    changePage,
    changeSort,
  } = useContext(TransactionsTableContext)

  const updateVariables = () => {
    return {
      ...prepareDatesForApi(filters.startDate, filters.endDate),
      skip: (page - 1) * size,
      take: size,
      sorts: sort.by && [
        {
          [sort.by]: sort.dir.toUpperCase(),
        },
      ],
      externalPropertyId: user.activeWebsite.id.toString(),
      dateType: filters.dateType,
      event: filters.event,
      filters: {
        ...(filters.vertical && { externalVerticalId: filters.vertical }),
        ...(filters.productType && {
          externalProductTypeId: filters.productType,
        }),
        ...(filters.program && { externalProgramId: filters.program }),
        ...(filters.product && { externalDisplayedProductId: filters.product }),
        ...(filters.clickedProduct && {
          externalClickedProductId: filters.clickedProduct,
        }),
        ...(filters.campaign && { externalCampaignId: filters.campaign }),
        ...(filters.subTracking && { subTracking: filters.subTracking }),
      },
    } as TransactionDetailsReportVariables
  }

  const fetchData = () => {
    getData({ variables: updateVariables() })
    updateTableColumns()
  }

  const fetchExport = () => {
    getExportData({
      variables: {
        ...updateVariables(),
        take: get(
          data,
          `transactionDetailsReporting.pagination.totalEntries`,
          0
        ),
      },
    })
  }

  useEffect(() => {
    dumpData(
      'transactionDetails',
      exportData?.transactionDetailsReporting?.page,
      {
        ...filters,
      }
    )
  }, [exportData])

  useEffect(fetchData, [sort, page, size, user.activeWebsite])

  const pagination = {
    currentPage: get(
      data,
      `transactionDetailsReporting.pagination.currentPage`,
      1
    ),
    pageSize: get(data, `transactionDetailsReporting.pagination.pageSize`, 10),
    total: get(data, `transactionDetailsReporting.pagination.totalEntries`, 0),
  }

  const allColumns = [
    {
      text: 'Click Date',
      sortable: true,
      field: 'clickedAt',
      render: row =>
        row.clickedAt && (
          <DateTimeDisplay datetime={DateTime.fromISO(row.clickedAt)} />
        ),
    },
    {
      text: 'Process Date',
      sortable: true,
      field: 'processedAt',
      render: row =>
        row.processedAt && (
          <DateTimeDisplay datetime={DateTime.fromISO(row.processedAt)} />
        ),
    },
    {
      text: 'Click ID',
      sortable: true,
      field: 'externalClickId',
    },
    {
      text: 'Vertical',
      sortable: true,
      field: 'verticalName',
    },
    {
      text: 'Product Type',
      sortable: true,
      field: 'typeName',
    },
    {
      text: 'Program',
      sortable: true,
      field: 'programName',
    },
    {
      text: 'Product Name',
      sortable: true,
      field: 'clickedProductName',
      render: ({ clickedProductName }) =>
        clickedProductName && decode(clickedProductName),
    },
    {
      text: 'Product ID',
      sortable: true,
      field: 'externalClickedProductId',
    },
    {
      text: 'Sale ID',
      sortable: true,
      field: 'saleId',
    },
    {
      text: 'Commissions',
      sortable: true,
      field: 'commission',
      render: ({ commission }) => accounting.formatMoney(commission, '$'),
    },
    {
      text: 'Event Type',
      sortable: true,
      field: 'eventType',
    },
    {
      text: 'IP Address',
      field: 'ipAddress',
    },
    {
      text: 'Page',
      field: 'page',
    },
    {
      text: 'Sub ID',
      field: 'subTracking',
    },
    {
      text: 'Credit Bucket',
      field: 'creditBucket',
    },
    {
      text: 'Cross Sale Indicator',
      field: 'crossSale',
    },
    {
      text: 'Loan Amount',
      field: 'loanAmount',
    },
    {
      text: 'State',
      field: 'postalState',
    },
  ]

  const [tableColumns, setTableColumns] = useState(allColumns)
  const [propertyHasData, setPropertyHasData] = useState(true)
  const [pageDescription, setPageDescription] = useState(
    'This report contains conversion event details for the filters selected.'
  )

  useEffect(() => {
    if (!propertyHasData) {
      setPageDescription('No reporting data available for this property yet.')
    }
  }, [propertyHasData])

  useEffect(() => {
    if (!propertyHasData) {
      window.location.reload()
    }
  }, [user.activeWebsite])

  const updateTableColumns = () => {
    if (filters.vertical === '3') {
      // Mortgages
      const availableColumns = [
        'clickedAt',
        'externalClickedId',
        'verticalName',
        'typeName',
        'productName',
        'externalProductId',
        'campaignName',
        'page',
        'subTracking',
        'commission',
        'processedAt',
        'creditBucket',
        'loan',
        'postalState',
      ]
      setTableColumns(
        allColumns.filter(c => availableColumns.includes(c.field))
      )
    } else if (filters.vertical === '5') {
      // Credit Cards
      const availableColumns = [
        'clickedAt',
        'externalClickedId',
        'verticalName',
        'typeName',
        'programName',
        'productName',
        'externalProductId',
        'campaignName',
        'subTracking',
        'page',
        'saleId',
        'commission',
        'processedAt',
        'eventType',
      ]
      setTableColumns(
        allColumns.filter(c => availableColumns.includes(c.field))
      )
    } else if (
      filters.vertical === '7' ||
      filters.vertical === '8' ||
      filters.vertical === '10' ||
      filters.vertical === '11' ||
      filters.vertical === '13'
    ) {
      // Investing || Health || Retail || Home Services || Personal Finance
      const availableColumns = [
        'clickedAt',
        'externalClickedId',
        'verticalName',
        'typeName',
        'programName',
        'productName',
        'externalProductId',
        'campaignName',
        'page',
        'subTracking',
        'commission',
        'processedAt',
      ]
      setTableColumns(
        allColumns.filter(c => availableColumns.includes(c.field))
      )
    } else if (
      filters.vertical === '4' ||
      filters.vertical === '6' ||
      filters.vertical === '9' ||
      filters.vertical === '12'
    ) {
      // Loans || Deposits || Insurance || Home Equity
      const availableColumns = [
        'clickedAt',
        'externalClickedId',
        'verticalName',
        'typeName',
        'campaignName',
        'page',
        'subTracking',
        'commission',
        'processedAt',
      ]
      // Loans
      if (filters.vertical === '4') {
        availableColumns.push('creditBucket')
      }
      setTableColumns(
        allColumns.filter(c => availableColumns.includes(c.field))
      )
    } else {
      setTableColumns(allColumns)
    }
  }

  return (
    <>
      <PageHeading title="Event Details" description={pageDescription}>
        {propertyHasData && (
          <TableFilters
            tableContext={TransactionsTableContext}
            showEvent={true}
            showClickedProducts={true}
            setPropertyHasData={setPropertyHasData}
          />
        )}
      </PageHeading>
      {propertyHasData && (
        <>
          <ReportActions
            fetchReport={fetchData}
            fetchExport={fetchExport}
            disabled={
              !get(
                data,
                `transactionDetailsReporting.pagination.totalEntries`,
                0
              )
            }
            reportStatus={{ loading: loading, hasError: error }}
            exportStatus={{ loading: exportLoading, hasError: exportError }}
          />

          <Table<TransactionDetail[]>
            cols={tableColumns}
            data={data && get(data, `transactionDetailsReporting.page`)}
            onSortChange={changeSort}
            sort={sort}
            pagination={{
              onPageSizeUpdate: changePageSize,
              onPaginate: changePage,
              ...pagination,
            }}
            isLoading={loading}
          />
        </>
      )}
    </>
  )
}

export default TransactionsTable
